import { useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { QuerySocketEvent } from '@trinity/chat';
import type { WS } from '@trinity/chat';
import { QuerySocketContext, SetDisplayModeContext } from '../Chat/context';
import { MessageTestingResultsContext } from '../MessageTestingGrid/ResultsContainer';

interface Props {
  children: ReactNode;
  queryIdentifier: string;
}

/**
 * Listens for `QueryClientAction` WebSocket events and dispatches them to the
 * appropriate context (SetDisplayModeContext, MessageTestingResultsContext).
 *
 * Mounted inside `AgentProgressContainer` so it shares the same query socket.
 */
export const QueryClientActionContainer = ({ children, queryIdentifier }: Props): ReactNode => {
  const socket = useContext(QuerySocketContext);
  const setDisplayMode = useContext(SetDisplayModeContext);
  const mtResults = useContext(MessageTestingResultsContext);

  const queryIdentifierRef = useRef(queryIdentifier);
  useEffect(() => {
    queryIdentifierRef.current = queryIdentifier;
  });

  const setDisplayModeRef = useRef(setDisplayMode);
  useEffect(() => {
    setDisplayModeRef.current = setDisplayMode;
  });

  const mtResultsRef = useRef(mtResults);
  useEffect(() => {
    mtResultsRef.current = mtResults;
  });

  useEffect(() => {
    if (!socket) return;

    const handleClientAction = (data: WS.QueryClientAction.Data) => {
      if (data.queryIdentifier !== queryIdentifierRef.current) return;

      switch (data.action.type) {
        case 'set_display_mode':
          setDisplayModeRef.current?.(data.action.mode);
          break;
        case 'open_testing_results_panel':
          mtResultsRef.current?.openResultsPanel(data.action.runIdentifier);
          break;
      }
    };

    socket.on(QuerySocketEvent.QueryClientAction, handleClientAction);

    return () => {
      socket.off(QuerySocketEvent.QueryClientAction, handleClientAction);
    };
  }, [socket]);

  return children;
};
